import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EngageConfirmDialog from '../Engage/EngageConfirmDialog';
import { appLeaveHref } from '../Engage/engageSession';
import { askUnderstand } from '../../queries/understand';
import { setNavigationGuard } from '../../utils/navigationGuard';
import { registerUnderstandLeave } from '../../utils/understandLeave';
import AnswerBody from './Understand/AnswerBody';
import {
  DISCARD_COPY,
  QUESTION_LIMIT,
  SUGGESTED_QUESTIONS,
  conversationPayload,
  hasUnderstandWork,
} from './Understand/understandSession';

const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

const ClientWorkspaceUnderstand = ({
  companyName = 'this company',
  productName = 'this product',
  clientId = '',
  programId = '',
  onBack,
}) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const scrollerRef = useRef(null);
  const stickRef = useRef(true);
  const sendingRef = useRef(false);
  const generationRef = useRef(0);
  const abortRef = useRef(null);
  const idRef = useRef(0);
  const hasWorkRef = useRef(false);

  const hasWork = hasUnderstandWork(messages, draft);
  hasWorkRef.current = hasWork;
  const started = messages.length > 0;

  const nextId = () => {
    idRef.current += 1;
    return `understand-${idRef.current}`;
  };

  const clearConversation = () => {
    generationRef.current += 1;
    abortRef.current?.abort();
    sendingRef.current = false;
    setPending(false);
    setMessages([]);
    setDraft('');
  };

  useEffect(() => {
    return registerUnderstandLeave((proceed) => {
      if (!hasWorkRef.current) return proceed();
      setPendingAction((current) => current || proceed);
      return undefined;
    });
  }, []);

  useEffect(() => {
    if (!hasWork) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasWork]);

  useEffect(() => {
    if (!hasWork) return undefined;
    return setNavigationGuard((href) => {
      setPendingAction(() => () => navigate(href));
      return false;
    });
  }, [hasWork, navigate]);

  useEffect(() => {
    if (!hasWork) return undefined;
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a');
      if (!anchor) return;
      const href = appLeaveHref(
        { href: anchor.href, target: anchor.target, download: anchor.hasAttribute('download') },
        window.location.href,
      );
      if (!href) return;
      event.preventDefault();
      event.stopPropagation();
      setPendingAction(() => () => navigate(href));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [hasWork, navigate]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !stickRef.current) return;
    scroller.scrollTop = scroller.scrollHeight;
  }, [messages, pending]);

  const runQuestion = async ({ id, question, history }) => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const controller = new AbortController();
    abortRef.current = controller;
    setPending(true);
    try {
      const response = await askUnderstand(
        { clientId, programId, question, messages: history },
        { signal: controller.signal },
      );
      if (generation !== generationRef.current) return;
      const answer = String(response?.data?.answer || '').trim();
      if (!answer) throw new Error('empty');
      const answerId = nextId();
      setMessages((current) => [
        ...current.map((item) => (item.id === id ? { ...item, failed: false, errorMessage: '' } : item)),
        { id: answerId, role: 'assistant', text: answer },
      ]);
    } catch (error) {
      if (generation !== generationRef.current || isCanceled(error)) return;
      const errorMessage = error?.response?.data?.message || "Couldn't answer this question";
      setMessages((current) =>
        current.map((item) => (item.id === id ? { ...item, failed: true, errorMessage } : item)),
      );
    } finally {
      if (generation === generationRef.current) {
        sendingRef.current = false;
        setPending(false);
      }
    }
  };

  const submit = (raw) => {
    const question = String(raw || '').trim();
    if (!question || sendingRef.current) return;
    if (!clientId || !programId) {
      toast('This opportunity is missing a client or product.', { type: 'error' });
      return;
    }
    const id = nextId();
    const history = conversationPayload(messages);
    sendingRef.current = true;
    stickRef.current = true;
    setDraft('');
    setMessages((current) => [...current, { id, role: 'user', text: question, failed: false, errorMessage: '' }]);
    runQuestion({ id, question, history });
  };

  const retry = (message) => {
    if (sendingRef.current) return;
    const index = messages.findIndex((item) => item.id === message.id);
    const history = conversationPayload(messages.slice(0, Math.max(index, 0)));
    sendingRef.current = true;
    stickRef.current = true;
    setMessages((current) =>
      current.map((item) => (item.id === message.id ? { ...item, failed: false, errorMessage: '' } : item)),
    );
    runQuestion({ id: message.id, question: message.text, history });
  };

  const requestDiscard = (proceed) => {
    if (!hasWorkRef.current) {
      proceed();
      return;
    }
    setPendingAction(() => proceed);
  };

  const confirmDiscard = () => {
    const proceed = pendingAction;
    setPendingAction(null);
    clearConversation();
    proceed?.();
  };

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    stickRef.current = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80;
  };

  const onDraftKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit(draft);
    }
  };

  const hint = draft.length >= QUESTION_LIMIT ? '8,000 character limit' : 'Enter to send, Shift+Enter for a new line';

  const composer = (
    <Box>
      <TextField
        fullWidth
        multiline
        minRows={started ? 1 : 3}
        maxRows={8}
        placeholder={started ? 'Ask a follow-up...' : 'Ask anything...'}
        value={draft}
        autoFocus
        onChange={(event) => setDraft(event.target.value.slice(0, QUESTION_LIMIT))}
        onKeyDown={onDraftKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end' sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
              <IconButton
                aria-label='Send'
                onClick={() => submit(draft)}
                disabled={pending || !draft.trim()}
                sx={{
                  bgcolor: 'primary.main',
                  color: '#fff',
                  width: 36,
                  height: 36,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: '#fff' },
                }}
              >
                <ArrowUpwardIcon fontSize='small' />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            bgcolor: '#fff',
            alignItems: 'flex-end',
            pr: 1,
          },
        }}
      />
      <Typography variant='caption' color='text.secondary' display='block' mt={0.75}>
        {hint}
      </Typography>
    </Box>
  );

  return (
    <Box display='flex' flexDirection='column' height='100%' minHeight={0}>
      <Box px={{ xs: 2, sm: 4 }} pt={3} pb={1} flexShrink={0}>
        <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={2}>
          <Box minWidth={0}>
            <Link
              component='button'
              type='button'
              underline='hover'
              variant='body2'
              fontWeight={600}
              onClick={() => requestDiscard(onBack)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                p: 0,
                color: 'primary.main',
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Back to actions
            </Link>
            <Typography variant='h6' fontWeight={700} mt={2} lineHeight={1.3}>
              Understand
            </Typography>
            <Typography variant='body2' color='text.secondary' noWrap>
              {companyName} · {productName}
            </Typography>
          </Box>
          {started ? (
            <Button
              variant='outlined'
              color='inherit'
              startIcon={<AddIcon />}
              onClick={() => requestDiscard(() => {})}
              sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, borderColor: 'divider' }}
            >
              New chat
            </Button>
          ) : null}
        </Box>
      </Box>

      {started ? (
        <Box
          ref={scrollerRef}
          flex={1}
          minHeight={0}
          overflow='auto'
          px={{ xs: 2, sm: 4 }}
          py={2}
          onScroll={handleScroll}
        >
          <Box maxWidth={760} mx='auto'>
            {messages.map((message) => {
              const isUser = message.role === 'user';
              return (
                <Box
                  key={message.id}
                  display='flex'
                  flexDirection='column'
                  alignItems={isUser ? 'flex-end' : 'flex-start'}
                  mb={2}
                >
                  <Box display='flex' alignItems='flex-start' gap={1} maxWidth='92%'>
                    {isUser ? null : <AutoAwesomeIcon color='primary' sx={{ fontSize: 18, mt: 1.25, flexShrink: 0 }} />}
                    <Box
                      bgcolor={isUser ? 'primary.main' : '#fff'}
                      color={isUser ? '#fff' : 'text.primary'}
                      border={isUser ? 0 : 1}
                      borderColor='divider'
                      borderRadius={2}
                      px={2}
                      py={1.25}
                      sx={{ overflowWrap: 'anywhere', whiteSpace: isUser ? 'pre-wrap' : 'normal' }}
                    >
                      {isUser ? (
                        <Typography variant='body2' sx={{ lineHeight: 1.7, color: 'inherit' }}>
                          {message.text}
                        </Typography>
                      ) : (
                        <AnswerBody text={message.text} />
                      )}
                    </Box>
                  </Box>
                  {message.failed ? (
                    <Box textAlign='right' mt={0.75}>
                      <Typography variant='body2' color='error'>
                        {message.errorMessage || "Couldn't answer this question"}
                      </Typography>
                      <Button
                        onClick={() => retry(message)}
                        disabled={pending}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 0, minWidth: 0 }}
                      >
                        Try again
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
            {pending ? (
              <Box display='flex' alignItems='center' gap={1} mb={2}>
                <AutoAwesomeIcon color='primary' sx={{ fontSize: 18 }} />
                <Box
                  border={1}
                  borderColor='divider'
                  borderRadius={2}
                  bgcolor='#fff'
                  px={2}
                  py={1.25}
                  display='flex'
                  alignItems='center'
                  gap={1}
                >
                  <CircularProgress size={16} />
                  <Typography variant='body2' color='text.secondary'>
                    Writing an answer...
                  </Typography>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box
          flex={1}
          minHeight={0}
          overflow='auto'
          display='flex'
          flexDirection='column'
          justifyContent='center'
          px={{ xs: 2, sm: 4 }}
          pb={4}
        >
          <Box width='100%' maxWidth={680} mx='auto'>
            <Typography variant='h5' fontWeight={700} textAlign='center' mb={2.5}>
              What can I help you with?
            </Typography>
            {composer}
            <Box display='flex' flexWrap='wrap' justifyContent='center' gap={1} mt={2}>
              {SUGGESTED_QUESTIONS.map((question) => (
                <Button
                  key={question}
                  variant='outlined'
                  color='inherit'
                  disabled={pending}
                  onClick={() => submit(question)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 5,
                    borderColor: 'divider',
                    color: 'text.primary',
                    px: 1.75,
                    py: 0.5,
                    bgcolor: '#fff',
                    '&:hover': { borderColor: 'primary.light', bgcolor: '#f5f9ff' },
                  }}
                >
                  {question}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {started ? (
        <Box flexShrink={0} px={{ xs: 2, sm: 4 }} py={2} borderTop={1} borderColor='divider' bgcolor='#fff'>
          <Box maxWidth={760} mx='auto'>
            {composer}
          </Box>
        </Box>
      ) : null}

      <EngageConfirmDialog
        open={Boolean(pendingAction)}
        title={DISCARD_COPY.title}
        body={DISCARD_COPY.body}
        cancelLabel={DISCARD_COPY.cancel}
        confirmLabel={DISCARD_COPY.confirm}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmDiscard}
      />
    </Box>
  );
};

export default ClientWorkspaceUnderstand;
